import { useEffect, useRef, useState } from "react";
import type { FocusEvent, ReactNode } from "react";

interface AccountMenuProps {
  /** Used both for the avatar's initial and the trigger's accessible name. */
  email: string;
  /**
   * Same shape as Layout's `accountSlot` render prop — called with a
   * function that closes this menu, so its buttons (e.g. the edit-mode
   * toggle) can dismiss it immediately rather than leaving it open over
   * whatever they just did.
   */
  children: (closeMenu: () => void) => ReactNode;
}

/**
 * The desktop header's avatar button + dropdown, replacing what used to be
 * the email/edit-toggle/log-out buttons sitting inline in the header (see
 * app.css's history) — those three unrelated pieces crammed into one row
 * grew unpredictably with a longer email. Collapses them behind a single
 * fixed-width avatar; the panel reuses the exact same `.nav-drawer__account`
 * content the mobile drawer renders, just repositioned into a popover.
 *
 * Not a full ARIA `menu` (no arrow-key navigation) — its contents are a
 * couple of ordinary buttons, not a list of commands, so a plain disclosure
 * pattern (button + `aria-expanded`, Tab through the panel normally) is a
 * better fit than the stricter `role="menu"` widget contract.
 */
export function AccountMenu({ email, children }: AccountMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  function close() {
    setIsOpen(false);
    buttonRef.current?.focus();
  }

  useEffect(() => {
    if (!isOpen) return;

    // Focus the panel's first control so keyboard users land somewhere
    // useful immediately, matching the drawer's focus-on-open behavior.
    const firstFocusable = panelRef.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    firstFocusable?.focus();

    function handlePointerDown(e: PointerEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false);
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }
    document.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  // Tabbing past the panel's last item should close it too, not just an
  // outside click/Escape — a plain popover (unlike the drawer) doesn't trap
  // focus, so this is what keeps it from staying open once focus has moved
  // on to the rest of the page.
  function handleBlur(e: FocusEvent<HTMLDivElement>) {
    if (!containerRef.current?.contains(e.relatedTarget)) setIsOpen(false);
  }

  return (
    <div className="account-menu" ref={containerRef} onBlur={handleBlur}>
      <button
        ref={buttonRef}
        type="button"
        className="account-menu__avatar"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={`Account menu for ${email}`}
        onClick={() => setIsOpen((v) => !v)}
      >
        {email.charAt(0).toUpperCase()}
      </button>
      {isOpen && (
        <div ref={panelRef} className="account-menu__panel">
          <div className="nav-drawer__account">{children(close)}</div>
        </div>
      )}
    </div>
  );
}
